import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  Search, 
  Trash2, 
  RefreshCw, 
  AlertTriangle,
  UserX,
  Settings,
  Filter,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { dataService, User } from '@/services/dataService';
import { useRoleAccess } from '@/hooks/useRoleAccess';

interface ExtendedUser extends User {
  interests: string[];
  stats: {
    completedCourses: number;
    inProgressCourses: number;
    totalCourses: number;
  };
  lastActive: string;
  status: 'active' | 'inactive';
}

export function UserManagementTab() {
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'reset' | 'delete' | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const roleAccess = useRoleAccess();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setInitialLoading(true);
      
      // Get users from the real data service
      const profiles = await dataService.getUsers();
      
      if (profiles.length === 0) {
        setUsers([]);
        toast.info('No users found');
        return;
      }

      // Enrich user data with interests and progress
      const enrichedUsers: ExtendedUser[] = await Promise.all(
        profiles.map(async (profile) => {
          try {
            // Get user interests
            const interests = await dataService.getUserInterests(profile.id);
            
            // Get user progress
            const progress = await dataService.getUserProgress(profile.id);
            
            const completedCount = progress.filter(p => p.status === 'completed').length;
            const inProgressCount = progress.filter(p => p.status === 'in_progress').length;
            
            return {
              ...profile,
              interests,
              stats: {
                completedCourses: completedCount,
                inProgressCourses: inProgressCount,
                totalCourses: progress.length
              },
              lastActive: profile.updated_at,
              status: 'active' as const
            };
          } catch (error) {
            console.warn(`Failed to enrich data for user ${profile.id}:`, error);
            return {
              ...profile,
              interests: [],
              stats: {
                completedCourses: 0,
                inProgressCourses: 0,
                totalCourses: 0
              },
              lastActive: profile.updated_at,
              status: 'active' as const
            };
          }
        })
      );

      setUsers(enrichedUsers);
      toast.success(`Successfully loaded ${enrichedUsers.length} users`);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users. Please check your permissions.');
      setUsers([]);
    } finally {
      setInitialLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleResetUserInterests = async (userId: string) => {
    if (!roleAccess.canResetUserInterests) {
      toast.error('You do not have permission to reset user interests');
      return;
    }

    setLoading(true);
    try {
      const success = await dataService.resetUserInterests(userId);
      
      if (success) {
        // Update local state
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { 
                ...user, 
                interests: [], 
                stats: { ...user.stats, completedCourses: 0, inProgressCourses: 0, totalCourses: 0 }
              }
            : user
        ));
      }
    } catch (error) {
      console.error('Error resetting user interests:', error);
      toast.error('Failed to reset user interests');
    } finally {
      setLoading(false);
      setSelectedUser(null);
      setActionType(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!roleAccess.canDeleteUsers) {
      toast.error('You do not have permission to delete users');
      return;
    }

    setLoading(true);
    try {
      const success = await dataService.deleteUser(userId);
      
      if (success) {
        // Remove user from local state
        setUsers(prev => prev.filter(user => user.id !== userId));
        toast.success('User deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
      setSelectedUser(null);
      setActionType(null);
    }
  };

  const openConfirmDialog = (userId: string, action: 'reset' | 'delete') => {
    setSelectedUser(userId);
    setActionType(action);
  };

  const closeConfirmDialog = () => {
    setSelectedUser(null);
    setActionType(null);
  };

  const executeAction = () => {
    if (!selectedUser || !actionType) return;

    if (actionType === 'reset') {
      handleResetUserInterests(selectedUser);
    } else if (actionType === 'delete') {
      handleDeleteUser(selectedUser);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin': return 'destructive';
      case 'admin': return 'destructive';
      case 'editor': return 'default';
      case 'contributor': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      default: return 'outline';
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-lg font-semibold text-black">User Management</h2>
        </div>
        <Button 
          onClick={loadUsers} 
          disabled={loading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Users</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, username, or email..."
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Users ({filteredUsers.length})</span>
            <div className="text-sm font-normal text-gray-600">
              Total registered users
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-black">
                          {user.full_name?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-black">
                          {user.full_name || user.username || 'No name'}
                        </h3>
                        <p className="text-sm text-gray-600">{user.email || 'Email protected'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {user.role.replace('_', ' ')}
                        </Badge>
                        <Badge variant={getStatusBadgeVariant(user.status)}>
                          {user.status}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Interests:</span>
                        <div className="mt-1">
                          {user.interests.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.interests.slice(0, 3).map((interest, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {interest}
                                </Badge>
                              ))}
                              {user.interests.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{user.interests.length - 3} more
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">No interests set</span>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Progress:</span>
                        <div className="mt-1">
                          <span className="text-black">
                            {user.stats.completedCourses} completed, {user.stats.inProgressCourses} in progress
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Joined:</span>
                        <div className="mt-1">
                          <span className="text-black">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {}}>
                          <Settings className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {roleAccess.canResetUserInterests && (
                          <DropdownMenuItem 
                            onClick={() => openConfirmDialog(user.id, 'reset')}
                            className="text-orange-600"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Reset Interests
                          </DropdownMenuItem>
                        )}
                        {roleAccess.canDeleteUsers && (
                          <DropdownMenuItem 
                            onClick={() => openConfirmDialog(user.id, 'delete')}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-8">
                <UserX className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-black mb-2">No users found</h3>
                <p className="text-gray-600">
                  {searchTerm ? 'Try adjusting your search criteria.' : 'No users have been registered yet.'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!selectedUser && !!actionType} onOpenChange={closeConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {actionType === 'delete' ? 'Delete User' : 'Reset User Interests'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === 'delete' 
                ? 'This action cannot be undone. This will permanently delete the user account and all associated data.'
                : 'This will reset all user interests, progress, and preferences. The user will need to set up their interests again.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeConfirmDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeAction}
              className={actionType === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}
              disabled={loading}
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : actionType === 'delete' ? (
                <Trash2 className="h-4 w-4 mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {actionType === 'delete' ? 'Delete User' : 'Reset Interests'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
